import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Card, DataList, PageHeader, StatusBadge, type DataColumn } from "@nahal/ui";
import type { SearchHit } from "@nahal/api-client";
import { useSearch } from "../../lib/queries";

/**
 * The front door. A customer on the phone does not know which product is
 * failing, so there is one box that matches an organisation, an email or a
 * domain rather than a filter per field.
 */
export function SearchScreen() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const results = useSearch(term);

  const columns: Array<DataColumn<SearchHit>> = [
    { key: "name", header: "Customer", primary: true, cell: (row) => row.name },
    { key: "email", header: "Billing email", cell: (row) => row.billingEmail ?? "-" },
    { key: "status", header: "Status", cell: (row) => <StatusBadge status={row.status} /> },
    { key: "workspaces", header: "Workspaces", numeric: true, cell: (row) => row.workspaceCount },
  ];

  return (
    <>
      <PageHeader title="Customers" description="Organisation, email, workspace code or domain." />

      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search customers"
          aria-label="Search customers"
          className="min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--border-default)] bg-[var(--surface-card)] pl-9 pr-3 text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] sm:text-sm"
        />
      </div>

      <Card>
        <DataList
          columns={columns}
          rows={results.data ?? []}
          rowKey={(row) => row.organizationId}
          loading={results.isLoading}
          error={results.isError ? "Search failed." : null}
          empty={{ title: term.trim() ? "No match" : "No customers", description: term.trim() ? "Nothing found for that term." : "No customer organisations have been created yet." }}
          onRowClick={(row) =>
            navigate({ to: "/customers/$organizationId", params: { organizationId: row.organizationId } })
          }
        />
      </Card>
    </>
  );
}
